/**
 * «Stampa» (voce del bottone Condividi, entrambe le vesti): sceglie come avere
 * il ticket (PDF da scaricare o stampante), se includere gli allegati e, a chi
 * vede le note interne, se includerle (spuntate di default). Il PDF lo prepara il
 * server (`printTicket` dell'adapter); qui si scarica o si manda alla stampa del
 * browser (`printTicket.ts`).
 *
 * - Chiusura o «Annulla» durante la preparazione: al ritorno del server non si
 *   scarica né si stampa nulla (e l'eventuale scheda vuota si chiude).
 * - «Stampante» su Safari e su telefoni/tablet: la scheda per il PDF si apre
 *   subito al clic (gesto dell'utente) e riceve il PDF quando è pronto.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Download, Printer, X } from 'lucide-react';
import { S3Modal } from '../v3/primitives';
import { useToast } from '../../../contexts/ToastContext';
import { canShareNotes, type ShareEmailTicket } from './shareEmailModel';
import {
  closePendingTab,
  downloadPdf,
  openPendingTab,
  printNeedsTab,
  printPdfBlob,
  ticketPrintFilename,
  type PrintHow,
} from '../printTicket';
import { printErrorMessage } from '../../../services/supportPrint';
import type { TicketPrintFile, TicketPrintOptions } from '../../../services/supportApi';
import '../../../styles/admin-v2.css';
import '../../../styles/support-v3.css';

export interface PrintTicketModalProps {
  ticket: ShareEmailTicket;
  onClose: () => void;
  printTicket: (options: TicketPrintOptions) => Promise<TicketPrintFile>;
  /** L'utente vede le note interne nella scheda (admin: `supporttickets.handle`). */
  notesVisible: boolean;
}

const HOW_OPTIONS: Array<{ value: PrintHow; label: string; sub: string }> = [
  { value: 'pdf', label: 'PDF (scarica il file)', sub: 'Da conservare, inoltrare o stampare in seguito' },
  { value: 'printer', label: 'Stampante', sub: 'Si apre la finestra di stampa del browser' },
];

/** Toast se c'e il provider; senza, i messaggi passano dalla regione dell'avviso. */
const useToastIfAvailable = (): ReturnType<typeof useToast> | undefined => {
  try {
    return useToast();
  } catch {
    return undefined;
  }
};

const PrintTicketModal: React.FC<PrintTicketModalProps> = ({ ticket, onClose, printTicket, notesVisible }) => {
  const toast = useToastIfAvailable();
  const [how, setHow] = useState<PrintHow | null>(null);
  const [attachments, setAttachments] = useState(false);
  const [notes, setNotes] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; url?: string; error?: boolean } | null>(null);
  const notesAvailable = notesVisible && canShareNotes(ticket);
  /** Popup chiuso o annullato: le risposte in arrivo non fanno più nulla. */
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);
  const isCancelled = () => cancelledRef.current;

  const close = () => {
    cancelledRef.current = true;
    onClose();
  };

  const run = async () => {
    if (!how || busy) return;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    // Safari e mobile: la scheda si apre ORA, nel clic, e riceve il PDF dopo.
    const useTab = how === 'printer' && printNeedsTab(ua);
    const pendingTab = useTab ? openPendingTab() : undefined;
    setBusy(true);
    setNotice(null);
    try {
      const file = await printTicket({ attachments, notes: notesAvailable && notes });
      if (cancelledRef.current) {
        closePendingTab(pendingTab);
        return;
      }
      const name = file.filename || ticketPrintFilename(ticket);
      if (how === 'pdf') {
        downloadPdf(file.blob, name);
        if (toast) toast.success('PDF scaricato');
        close();
        return;
      }
      const out = await printPdfBlob(file.blob, ua, { ...(useTab ? { pendingTab } : {}), isCancelled });
      if (out.mode === 'cancelled' || cancelledRef.current) return;
      if (out.mode === 'dialog') {
        close();
        return;
      }
      if (out.mode === 'tab') {
        if (toast) toast.info('PDF aperto in una nuova scheda', 'Usa «Stampa» del browser per stamparlo.');
        close();
        return;
      }
      setNotice({ text: 'Il browser non ha aperto la stampa: apri il PDF e usa «Stampa» del browser.', url: out.url });
    } catch (err) {
      closePendingTab(pendingTab);
      if (cancelledRef.current) return;
      const msg = await printErrorMessage(err);
      if (cancelledRef.current) return;
      if (toast) toast.error('Stampa non riuscita', msg);
      setNotice({ text: msg, error: true });
    } finally {
      if (!cancelledRef.current) setBusy(false);
    }
  };

  const confirmLabel = busy ? 'Preparazione in corso…' : how === 'pdf' ? 'Scarica PDF' : 'Stampa';

  return (
    <S3Modal open onClose={close} labelledBy="s4-print-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s4-print-title">Stampa il ticket</h3>
          <button type="button" className="s3-pill s3-pill--sm s3-pill--icon" onClick={close} aria-label="Chiudi">
            <X size={14} />
          </button>
        </div>

        <div className="s3-mod__body">
          <fieldset className="s3-field" style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="s3-label" style={{ marginBottom: 5 }}>
              Come
            </legend>
            <div className="s3-pick" role="radiogroup" aria-required="true">
              {HOW_OPTIONS.map((o) => (
                <label key={o.value} className={`s3-pick__opt${how === o.value ? ' s3-pick__opt--on' : ''}`}>
                  <input
                    type="radio"
                    name="s4-print-how"
                    value={o.value}
                    checked={how === o.value}
                    onChange={() => setHow(o.value)}
                    required
                  />
                  <span className="s3-pick__txt">
                    {o.label}
                    <span className="s3-pick__sub">{o.sub}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="s3-pick">
            <label className={`s3-pick__opt${attachments ? ' s3-pick__opt--on' : ''}`}>
              <input type="checkbox" checked={attachments} onChange={(e) => setAttachments(e.target.checked)} />
              <span className="s3-pick__txt">
                Includi gli allegati
                <span className="s3-pick__sub">
                  Immagini sotto il loro messaggio, PDF in fondo al documento, altri file solo elencati
                </span>
              </span>
            </label>
            {notesAvailable && (
              <label className={`s3-pick__opt${notes ? ' s3-pick__opt--on' : ''}`}>
                <input type="checkbox" checked={notes} onChange={(e) => setNotes(e.target.checked)} />
                <span className="s3-pick__txt">
                  Includi le note interne
                  <span className="s3-pick__sub">Visibili solo al canale: attenzione a chi consegni il documento</span>
                </span>
              </label>
            )}
          </div>

          {busy && (
            <p className="s3-mod__note" role="status">
              Il server sta preparando il PDF: con molti allegati può volerci qualche secondo.
            </p>
          )}
          {notice && (
            <p className={notice.error ? 's3-mod__warn' : 's3-mod__note'} role={notice.error ? 'alert' : 'status'}>
              {notice.text}
              {notice.url && (
                <>
                  {' '}
                  <a href={notice.url} target="_blank" rel="noopener noreferrer">
                    Apri il PDF
                  </a>
                </>
              )}
            </p>
          )}
        </div>

        <div className="s3-mod__foot">
          <button type="button" className="s3-pill" onClick={close}>
            Annulla
          </button>
          <button
            type="button"
            className="s3-pill s3-pill--primary"
            onClick={() => void run()}
            disabled={!how || busy}
            aria-busy={busy || undefined}
            title={!how ? 'Scegli prima «PDF» o «Stampante»' : undefined}
          >
            {how === 'pdf' ? <Download size={13} aria-hidden="true" /> : <Printer size={13} aria-hidden="true" />} {confirmLabel}
          </button>
        </div>
      </div>
    </S3Modal>
  );
};

export default PrintTicketModal;
